import * as React from "react";
import { TableCell } from "@mui/material";
import { StatusCard } from "@/components/dashboard/StatusCard";
import { QueryStateBoundary } from "@/components/dashboard/QueryStateBoundary";
import { DataTable, type DataTableColumn } from "@/components/dashboard/DataTable";
import { HealthBadge } from "@/components/dashboard/HealthBadge";
import { useBrokerList } from "@/hooks/queries/useBrokerStatus";

interface BrokerSummary {
  provider_id: string;
  name: string;
  category: string;
  status: string;
  capabilities: string[];
}

const notReported = <span style={{ fontStyle: "italic", opacity: 0.6 }}>Not reported by backend</span>;

export function ConfiguredProvidersSection(): React.JSX.Element {
  const brokersQuery = useBrokerList();

  const columns: Array<DataTableColumn<BrokerSummary>> = [
    {
      key: "name",
      label: "Provider Name",
      sortable: true,
      sortValue: (row) => row.name,
      render: (row) => <TableCell key="name">{row.name}</TableCell>,
    },
    {
      key: "category",
      label: "Provider Type",
      sortable: true,
      sortValue: (row) => row.category,
      render: (row) => <TableCell key="category">{row.category}</TableCell>,
    },
    {
      key: "capabilities",
      label: "Capabilities",
      render: (row) => <TableCell key="capabilities">{row.capabilities.join(", ")}</TableCell>,
    },
    {
      // BrokerSummaryDto carries no distinct provider version field.
      key: "version",
      label: "Version",
      render: () => <TableCell key="version">{notReported}</TableCell>,
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <TableCell key="status">
          <HealthBadge status={row.status} />
        </TableCell>
      ),
    },
  ];

  return (
    <StatusCard title="Configured Providers">
      <QueryStateBoundary
        query={brokersQuery}
        isEmpty={(data) => (data as unknown as BrokerSummary[]).length === 0}
        emptyMessage="No broker providers are configured."
      >
        {(payload) => (
          <DataTable
            ariaLabel="Configured Providers"
            columns={columns}
            rows={payload as unknown as BrokerSummary[]}
            getRowKey={(row) => row.provider_id}
          />
        )}
      </QueryStateBoundary>
    </StatusCard>
  );
}
